import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import { format } from 'date-fns';
import { FaCheck, FaUndo, FaEdit } from 'react-icons/fa';
import { FaBarsProgress } from 'react-icons/fa6';
import ElevatedButton from '../Layout/ElevatedButton';
import AssignmentForm from './AssignmentForm';
import Modal from '../Layout/Modal';

const WarningText = styled.p`
  color: ${props => props.theme.statusPending};
  font-weight: 500;
  margin-bottom: 1rem;
`;

const GenericConfirmModal = ({ isOpen, onClose, title, message, confirmText = 'Confirm', cancelText = 'Cancel', onConfirm, confirmButtonColor = 'var(--color-error)' }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <div>
        <p style={{ color: 'var(--color-text)', marginBottom: '1rem', fontSize: '1rem' }}>
          {message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--color-textMuted)',
              color: '#ffffff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#5a6268'}
            onMouseOut={(e) => e.target.style.background = 'var(--color-textMuted)'}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: confirmButtonColor,
              color: '#ffffff',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = confirmButtonColor === 'var(--color-error)' ? '#c82333' : '#4a5568'}
            onMouseOut={(e) => e.target.style.background = confirmButtonColor}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 0;
  max-width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Item = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.25rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  color: ${props => props.theme.text};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const AssignmentInfo = styled.div`
  flex: 1;
  margin-bottom: ${props => props.compact ? '0.125rem' : '1rem'};
`;

const CourseBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  margin-right: 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.color || props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  font-family: 'Consolas', 'Monaco', monospace;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return props.theme.success;
      case 'in-progress': return props.theme.warning;
      case 'overdue': return props.theme.error;
      case 'pending': return props.theme.info;
      default: return props.theme.info;
    }
  }};
  color: ${props => props.theme.text};
`;

const GradeInput = styled.input`
  width: 100px;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

const ErrorText = styled.div`
  color: ${props => props.theme.error};
  fontSize: 0.75rem;
  fontWeight: 600;
  margin-top: 0.25rem;
`;

const GradeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.grade) {
      case 'A': return '#28a745';
      case 'A-': return '#5cb85c';
      case 'B+': return '#8bc34a';
      case 'B': return '#c0ca33';
      case 'B-': return '#fbc02d';
      case 'C+': return '#ff9800';
      case 'C': return '#ff6f00';
      case 'C-': return '#ff5722';
      case 'D+': return '#f44336';
      case 'D': return '#e53935';
      case 'D-': return '#d32f2f';
      case 'F': return '#b71c1c';
      default: return '#6c757d';
    }
  }};
  color: ${props => props.theme.text};
`;

const CompleteButton = styled(ElevatedButton)`
  background: ${props => props.theme.buttonSuccess};
  border: 1px solid ${props => props.theme.buttonSuccess};

  &:hover {
    background: ${props => props.theme.buttonSuccessHover};
    border: 1px solid ${props => props.theme.buttonSuccessHover};
  }
`;

const EditButton = styled(ElevatedButton)`
  background: ${props => props.theme.buttonPrimary};
  border: 1px solid ${props => props.theme.buttonPrimary};

  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
    border: 1px solid ${props => props.theme.buttonPrimaryHover};
  }
`;

const DeleteButton = styled(ElevatedButton)`
  background: ${props => props.theme.buttonDanger};
  border: 1px solid ${props => props.theme.buttonDanger};

  &:hover {
    background: ${props => props.theme.buttonDangerHover};
    border: 1px solid ${props => props.theme.buttonDangerHover};
  }
`;

const NotesButton = styled(ElevatedButton)`
  background: ${props => props.theme.buttonSecondary};
  border: 1px solid ${props => props.theme.buttonSecondary};

  &:hover {
    background: ${props => props.theme.buttonSecondaryHover};
    border: 1px solid ${props => props.theme.buttonSecondaryHover};
  }
`;

const InProgressButton = styled(ElevatedButton)`
  background: ${props => props.theme.warning || '#ffc107'};
  border: 1px solid ${props => props.theme.warning || '#ffc107'};
  color: #212529;

  &:hover {
    filter: brightness(0.9);
  }
`;

const AssignmentList = () => {
  const { assignments, courses, deleteAssignment, updateAssignment, settings } = useData();
  const navigate = useNavigate();
  const [gradeInputs, setGradeInputs] = useState({});
  const [gradeErrors, setGradeErrors] = useState({});
  const [editingGradeId, setEditingGradeId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  const validateGrade = (gradeStr) => {
    if (!gradeStr || gradeStr.trim() === '') return { isValid: true, error: '' };

    const grade = gradeStr.trim().toUpperCase();

    // Check for letter grades (including modifiers)
    if (/^(A|A-|B\+|B|B-|C\+|C|C-|D\+|D|D-|F)$/.test(grade)) {
      return { isValid: true, error: '' };
    }

    // Check for percentage format (number%)
    if (grade.endsWith('%')) {
      const numStr = grade.slice(0, -1);
      const num = parseFloat(numStr);
      if (isNaN(num)) {
        return { isValid: false, error: 'Invalid percentage number' };
      }
      if (num < 0 || num > 100) {
        return { isValid: false, error: 'Percentage must be between 0 and 100' };
      }
      if (!/^\d+(\.\d+)?$/.test(numStr)) {
        return { isValid: false, error: 'Invalid percentage format' };
      }
      return { isValid: true, error: '' };
    }

    // Check for fraction format
    if (grade.includes('/')) {
      const parts = grade.split('/');
      if (parts.length === 2) {
        const studentPoints = parseFloat(parts[0]);
        const totalPoints = parseFloat(parts[1]);

        if (isNaN(studentPoints) || isNaN(totalPoints)) {
          return { isValid: false, error: 'Invalid numbers in fraction' };
        }

        if (totalPoints <= 0) {
          return { isValid: false, error: 'Total points must be greater than 0' };
        }

        if (studentPoints > totalPoints) {
          return { isValid: false, error: 'Student points cannot exceed total points' };
        }

        if (studentPoints < 0) {
          return { isValid: false, error: 'Student points cannot be negative' };
        }

        if (!/^\d+(\.\d+)?$/.test(parts[0]) || !/^\d+(\.\d+)?$/.test(parts[1])) {
          return { isValid: false, error: 'Invalid fraction format' };
        }

        return { isValid: true, error: '' };
      }
    }

    return { isValid: false, error: 'Grade must be letter grade (A, A-, B+, B, B-, C+, C, C-, D+, D, D-, F), percentage (e.g., 85%), or fraction (e.g., 450/500)' };
  };

  const handleStatusChange = (assignmentId, newStatus, grade = null) => {
    if (grade) {
      const validation = validateGrade(grade);
      if (!validation.isValid) {
        setGradeErrors(prev => ({ ...prev, [assignmentId]: validation.error }));
        return;
      }
      // If a grade is provided, automatically mark as completed
      newStatus = 'completed';
    }

    // Find the assignment to get its current color or course color
    const assignment = assignments.find(a => a._id === assignmentId);
    const course = assignment ? getCourseInfo(assignment.course) : null;
    const color = assignment?.color || course?.color || '#5865f2';

    updateAssignment(assignmentId, { status: newStatus, grade, color });
    setGradeInputs(prev => ({ ...prev, [assignmentId]: '' }));
    setGradeErrors(prev => ({ ...prev, [assignmentId]: '' }));
  };

  const handleGradeInputChange = (assignmentId, value) => {
    setGradeInputs(prev => ({ ...prev, [assignmentId]: value }));

    const validation = validateGrade(value);
    setGradeErrors(prev => ({ ...prev, [assignmentId]: validation.error }));
  };

  const handleUpdateGrade = (assignmentId) => {
    const grade = gradeInputs[assignmentId];
    if (grade) {
      const validation = validateGrade(grade);
      if (!validation.isValid) {
        setGradeErrors(prev => ({ ...prev, [assignmentId]: validation.error }));
        return;
      }

      // Find the assignment to get its current color
      const assignment = assignments.find(a => a._id === assignmentId);
      const course = assignment ? getCourseInfo(assignment.course) : null;
      const color = assignment?.color || course?.color || '#5865f2';

      updateAssignment(assignmentId, { status: 'completed', grade, color });
      setGradeInputs(prev => ({ ...prev, [assignmentId]: '' }));
      setGradeErrors(prev => ({ ...prev, [assignmentId]: '' }));
    }
    setEditingGradeId(null);
  };

  const handleGradeClick = (assignmentId, currentGrade) => {
    setEditingGradeId(assignmentId);
    if (gradeInputs[assignmentId] === undefined) {
      setGradeInputs(prev => ({ ...prev, [assignmentId]: currentGrade || '' }));
    }
  };

  const handleGradeBlur = (assignmentId) => {
    handleUpdateGrade(assignmentId);
  };

  const handleGradeKeyDown = (e, assignmentId) => {
    if (e.key === 'Enter') {
      handleGradeBlur(assignmentId);
    } else if (e.key === 'Escape') {
      setEditingGradeId(null);
    }
  };

  const handleEdit = (assignment) => {
    setEditingAssignment(assignment);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingAssignment(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAssignment(null);
  };

  const handleFormSubmit = () => {
    setIsModalOpen(false);
    setEditingAssignment(null);
  };

  const handleDeleteAssignment = (assignment) => {
    setAssignmentToDelete(assignment);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!assignmentToDelete) return;

    try {
      await deleteAssignment(assignmentToDelete._id);
      setIsDeleteModalOpen(false);
      setAssignmentToDelete(null);
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      alert('Failed to delete assignment. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setAssignmentToDelete(null);
  };

  const handleOpenNotes = (assignment) => {
    const courseId = typeof assignment.course === 'object' && assignment.course?._id 
      ? assignment.course._id 
      : assignment.course;
    navigate(`/assignments/${courseId}/${assignment._id}`, { state: { assignment } });
  };

  const getCourseInfo = (courseId) => {
    return courses.find(course => course._id === courseId);
  };

  // Filter assignments based on settings - default to showing completed during loading
  const sortedAssignments = [...assignments]
    .filter(assignment => {
      // If showCompletedAssignments is true, show only active assignments; if false, show all
      const shouldShowCompleted = settings?.display?.showCompletedAssignments;
      if (shouldShowCompleted === undefined) return true;
      return !shouldShowCompleted || assignment.status !== 'completed';
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', flexShrink: 0 }}>
        <ElevatedButton onClick={handleAddNew}>Add Assignment</ElevatedButton>
      </div>
      <Container compact={settings?.display?.compactMode || false}>
        <Grid>
          {sortedAssignments.map(assignment => {
            const course = getCourseInfo(assignment.course);
            return (
              <Item key={assignment._id} borderColor={assignment.color || course?.color || '#5865f2'} compact={settings?.display?.compactMode || false}>
                <AssignmentInfo compact={settings?.display?.compactMode || false}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <h3 
                        style={{ 
                          margin: '0 0 0.25rem 0', 
                          color: 'var(--color-text)', 
                          fontSize: '1rem', 
                          fontWeight: 600,
                          cursor: 'pointer',
                          textDecoration: 'underline dotted',
                          textUnderlineOffset: '4px',
                          transition: 'text-decoration 0.2s',
                          outline: 'none',
                        }}
                        onClick={() => handleOpenNotes(assignment)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOpenNotes(assignment);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View notes for ${assignment.title}`}
                        onMouseOver={e => e.currentTarget.style.textDecoration = 'underline solid'}
                        onMouseOut={e => e.currentTarget.style.textDecoration = 'underline dotted'}
                        onFocus={e => e.currentTarget.style.textDecoration = 'underline solid'}
                        onBlur={e => e.currentTarget.style.textDecoration = 'underline dotted'}
                      >
                        {assignment.title}
                      </h3>
                    </div>
                    <StatusBadge status={assignment.status}>{assignment.status}</StatusBadge>
                  </div>
                  
                  {course && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <CourseBadge color={assignment.color || course.color}>{course.course_code}</CourseBadge>
                    </div>
                  )}
                  
                  <p style={{ margin: '0 0 0.75rem 0', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                    📅 Due: {assignment.due_date ? format(new Date(assignment.due_date), 'PPP') : 'No due date'}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)' }}>Grade:</span>
                    {editingGradeId === assignment._id ? (
                      <GradeInput
                        autoFocus
                        type="text"
                        placeholder="e.g., A-, 95%, 450/500"
                        value={gradeInputs[assignment._id] !== undefined ? gradeInputs[assignment._id] : (assignment.grade || '')}
                        onChange={(e) => handleGradeInputChange(assignment._id, e.target.value)}
                        onBlur={() => handleGradeBlur(assignment._id)}
                        onKeyDown={(e) => handleGradeKeyDown(e, assignment._id)}
                        style={{
                          borderColor: gradeErrors[assignment._id] ? '#f44336' : 'var(--color-border)',
                          margin: 0,
                          flex: 1
                        }}
                      />
                    ) : (
                      <div 
                        onClick={() => handleGradeClick(assignment._id, assignment.grade)}
                        style={{ 
                          cursor: 'pointer', 
                          flex: 1, 
                          padding: '0.25rem 0.5rem', 
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        title="Click to edit grade"
                      >
                        {assignment.grade ? (
                          <GradeBadge grade={assignment.grade}>
                            {assignment.grade}
                          </GradeBadge>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', fontSize: '0.875rem' }}>Add Grade</span>
                        )}
                        <FaEdit style={{ opacity: 0.5, fontSize: '0.875rem' }} />
                      </div>
                    )}
                  </div>
                  {gradeErrors[assignment._id] && (
                    <ErrorText>
                      {gradeErrors[assignment._id]}
                    </ErrorText>
                  )}

                  <ButtonRow compact={settings?.display?.compactMode || false}>
                    {assignment.status !== 'completed' && (
                      <CompleteButton
                        onClick={() => handleStatusChange(assignment._id, 'completed', gradeInputs[assignment._id] !== undefined ? gradeInputs[assignment._id] : assignment.grade)}
                        disabled={!!gradeErrors[assignment._id]}
                        title="Mark as Completed"
                        style={{
                          flex: 1,
                          opacity: gradeErrors[assignment._id] ? 0.5 : 1,
                          cursor: gradeErrors[assignment._id] ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <FaCheck /> Complete
                      </CompleteButton>
                    )}

                    {assignment.status !== 'in-progress' && (
                      <InProgressButton
                        onClick={() => handleStatusChange(assignment._id, 'in-progress')}
                        title={assignment.status === 'completed' ? "Mark as In Progress" : "Mark as In Progress"}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        {assignment.status === 'completed' ? <FaUndo /> : <FaBarsProgress />}
                        {assignment.status === 'completed' ? ' Undo' : ' In Progress'}
                      </InProgressButton>
                    )}

                    <EditButton onClick={() => handleEdit(assignment)}>Edit</EditButton>
                    <NotesButton onClick={() => handleOpenNotes(assignment)}>Notes</NotesButton>
                    <DeleteButton onClick={() => handleDeleteAssignment(assignment)}>Delete</DeleteButton>
                  </ButtonRow>
                </AssignmentInfo>
              </Item>
            );
          })}
        </Grid>
      </Container>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingAssignment ? 'Edit Assignment' : 'Add Assignment'}
      >
        <AssignmentForm
          assignment={editingAssignment}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseModal}
          isEditing={!!editingAssignment}
        />
      </Modal>

      {/* Assignment Delete Confirmation Modal */}
      <GenericConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Delete Assignment"
        message={
          <>
            Are you sure you want to delete <strong>"{assignmentToDelete?.title || ''}"</strong>?
            <br />
            <WarningText>
              This action cannot be undone.
            </WarningText>
          </>
        }
        confirmText="Delete Assignment"
        confirmButtonColor="#dc3545"
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default AssignmentList;
